import { createSignal, onCleanup, onMount, type JSX, For, Show } from "solid-js";
import { getPushState, subscribePush, unsubscribePush, type PushState } from "../shared/use-push";

export interface TweakSettings {
  theme: "dark" | "light";
  density: "default" | "compact" | "comfy";
  toolPanelPos: "bottom" | "right";
}

const LS_KEY = "rws.tweaks";

function loadTweaks(): TweakSettings {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (raw) return { ...defaultTweaks(), ...JSON.parse(raw) };
  } catch {}
  return defaultTweaks();
}

function defaultTweaks(): TweakSettings {
  return { theme: "dark", density: "default", toolPanelPos: "bottom" };
}

function saveTweaks(t: TweakSettings) {
  try {
    localStorage.setItem(LS_KEY, JSON.stringify(t));
  } catch {}
}

function applyTweaks(t: TweakSettings) {
  const root = document.documentElement;
  root.classList.toggle("theme-light", t.theme === "light");
  root.classList.remove("density-compact", "density-comfy");
  if (t.density === "compact") root.classList.add("density-compact");
  if (t.density === "comfy") root.classList.add("density-comfy");
}

// ─── Shared signal ─────────────────────────────────────────────────────────

const [tweaks, setTweaksRaw] = createSignal<TweakSettings>(defaultTweaks());

export function initTweaks() {
  const t = loadTweaks();
  setTweaksRaw(t);
  applyTweaks(t);
}

export function useTweaks() {
  return tweaks;
}

function setTweak<K extends keyof TweakSettings>(key: K, value: TweakSettings[K]) {
  const next = { ...tweaks(), [key]: value };
  setTweaksRaw(next);
  saveTweaks(next);
  applyTweaks(next);
}

// ─── TweaksPanel component ─────────────────────────────────────────────────

export function TweaksPanel(props: { onClose: () => void }) {
  const t = useTweaks();
  const [pushState, setPushState] = createSignal<PushState>(getPushState());
  const [pushHint, setPushHint] = createSignal<string | null>(null);

  async function togglePush() {
    setPushHint(null);
    const s = pushState();
    if (s === "subscribed") {
      await unsubscribePush();
      setPushState(getPushState());
    } else if (s === "denied") {
      setPushHint("Notifications bloquées au niveau du navigateur. Ouvrir les permissions du site et autoriser.");
    } else if (s === "unsupported") {
      setPushHint("Push non supporté par ce navigateur (iOS Safari < 16.4 par exemple).");
    } else {
      const r = await subscribePush();
      setPushState(getPushState());
      if (!r.ok) setPushHint(r.reason ?? "Échec inconnu");
    }
  }

  function RadioGroup(p: {
    label: string;
    options: { value: string; label: string }[];
    current: string;
    onChange: (v: string) => void;
  }): JSX.Element {
    return (
      <div class="tweaks-section">
        <div class="tweaks-section-label">{p.label}</div>
        <div class="tweaks-radio-group">
          <For each={p.options}>
            {(opt) => (
              <button
                class="tweaks-radio-btn"
                data-active={p.current === opt.value ? "true" : "false"}
                onClick={() => p.onChange(opt.value)}
              >
                {opt.label}
              </button>
            )}
          </For>
        </div>
      </div>
    );
  }

  // close on backdrop click
  function onOverlay(e: MouseEvent) {
    if ((e.target as HTMLElement).classList.contains("tweaks-overlay")) {
      props.onClose();
    }
  }

  onMount(() => {
    const esc = (e: KeyboardEvent) => {
      if (e.key === "Escape") props.onClose();
    };
    window.addEventListener("keydown", esc);
    onCleanup(() => window.removeEventListener("keydown", esc));
  });

  return (
    <>
      <div class="tweaks-overlay" onClick={onOverlay} />
      <div class="tweaks-popover">
        <div class="tweaks-head">Préférences</div>
        <RadioGroup
          label="Thème"
          current={t().theme}
          options={[
            { value: "dark", label: "Sombre" },
            { value: "light", label: "Clair" },
          ]}
          onChange={(v) => setTweak("theme", v as TweakSettings["theme"])}
        />
        <RadioGroup
          label="Densité"
          current={t().density}
          options={[
            { value: "compact", label: "Compact" },
            { value: "default", label: "Normal" },
            { value: "comfy", label: "Confort" },
          ]}
          onChange={(v) => setTweak("density", v as TweakSettings["density"])}
        />
        <RadioGroup
          label="Outils session"
          current={t().toolPanelPos}
          options={[
            { value: "bottom", label: "Bas" },
            { value: "right", label: "Droite" },
          ]}
          onChange={(v) => setTweak("toolPanelPos", v as TweakSettings["toolPanelPos"])}
        />

        {/* Notifications push — chat opérateur + alertes futures. État
            dérivé de l'API Notifications + localStorage (cf. use-push). */}
        <div class="tweaks-section">
          <div class="tweaks-section-label">Notifications (chat, alertes)</div>
          <div class="tweaks-radio-group">
            <button
              class="tweaks-radio-btn"
              data-active={pushState() === "subscribed" ? "true" : "false"}
              onClick={() => void togglePush()}
              disabled={pushState() === "unsupported"}
              title={
                pushState() === "subscribed"
                  ? "Désactiver les notifications"
                  : pushState() === "denied"
                    ? "Bloqué — autoriser dans les paramètres du navigateur"
                    : "Activer les notifications push"
              }
            >
              {pushState() === "subscribed"
                ? "✓ Activées"
                : pushState() === "denied"
                  ? "✕ Bloqué"
                  : pushState() === "unsupported"
                    ? "Non supporté"
                    : "Activer"}
            </button>
          </div>
          <Show when={pushHint()}>
            <p class="tweaks-hint">{pushHint()}</p>
          </Show>
        </div>
      </div>
    </>
  );
}
